import React, { useMemo, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilDropdown, { RingilDropdownItem } from '@components/RingilComponents/RingilDropdown.tsx';
import RingilCalendar from '@components/framework/input/RingilCalendar.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { dayjsFromLocalDateString, formatDateFromStringWithLocale } from '@utils/dateWithTz.tsx';
import { ExternalOrderResponse } from '@api/ringil-api.v2.tsx';
import { isNotBlank } from '@utils/utils.tsx';
import { NO_ORDERS_NOW_ORDER_NUMBER } from '@components/framework/orders/common.tsx';
import dayjs from 'dayjs';
import SvgBill from '@icons/bill.svg?react';
import SvgNoOrderNow from '@icons/bill-off.svg?react';
import SvgAlert from '@icons/alert-circle.svg?react';

export interface ExternalOrdersDropdownProps {
  availableOrders: ExternalOrderResponse[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  readonly?: boolean;
  dataTestIdBase?: string;
  // Whether to render the pinned NO_ORDERS_NOW option. Defaults to true.
  showNoOrdersNow?: boolean;
  // When true, orders whose [timeFrom, timeTo] span does not cover the current date are shown
  // greyed-out and can't be selected (SN create/edit rule, RD-2620). Orders without span info stay selectable.
  disableOrdersOutsideCurrentDate?: boolean;
}

// An order "spans the current date" when its [timeFrom, timeTo] interval overlaps today. Missing/invalid
// bounds are treated as open (an order with no span info always spans). Mirrors the frame used by `filterFn`.
const orderSpansToday = (o: ExternalOrderResponse): boolean => {
  const startOfToday = dayjs().startOf('day').valueOf();
  const endOfToday = dayjs().endOf('day').valueOf();
  const toMs = (iso?: string): number | null => {
    if (!iso) return null;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
  };
  const fromMs = toMs(o.timeFrom);
  const toMsVal = toMs(o.timeTo);
  if (fromMs !== null && fromMs > endOfToday) return false;
  if (toMsVal !== null && toMsVal < startOfToday) return false;
  return true;
};

// orderNumber is not unique across externalOrders, so synthesize an id from the array index.
export const buildExternalOrderId = (index: number, o: ExternalOrderResponse) => `${index}__${o.orderNumber}`;

/**
 * RingilDropdown wrapper for ExternalOrderResponse lists. Renders the NO_ORDERS_NOW
 * order as a pinned, mutually-exclusive option above "Select all", and provides a
 * single-date "in interval" secondary filter for time-bounded items.
 */
const ExternalOrdersDropdown: React.FC<ExternalOrdersDropdownProps> = ({
  availableOrders,
  selectedIds,
  onSelectionChange,
  readonly,
  dataTestIdBase,
  showNoOrdersNow = true,
  disableOrdersOutsideCurrentDate = false,
}) => {
  const { tr } = useTranslationLgs();
  const [filterDate, setFilterDate] = useState<string | null>(null);

  const items: RingilDropdownItem[] = useMemo(() => {
    // Map first so each item's id keeps the original availableOrders index, then drop
    // the NO_ORDERS_NOW option if it shouldn't be shown (preserves index alignment with the parent).
    return availableOrders
      .map((o, index): RingilDropdownItem | null => {
      if (o.orderNumber === NO_ORDERS_NOW_ORDER_NUMBER) {
        if (!showNoOrdersNow) return null;
        return {
          id: buildExternalOrderId(index, o),
          label: tr('ExternalOrders.noOrdersNow', 'No orders now'),
          subLabel: tr('ExternalOrders.noOrdersNowSubLabel', 'Reserve a timeslot without linking an order'),
          icon: <SvgNoOrderNow />,
          meta: { isNoOrdersNow: true },
        };
      }
      const pipe = <span className="text-content-tertiary">{'\u00A0|\u00A0'}</span>;
      const outsideCurrentDate = disableOrdersOutsideCurrentDate && !orderSpansToday(o);
      return {
        id: buildExternalOrderId(index, o),
        label: o.orderNumber,
        labelNode: (
          <span>
            {o.orderNumber}
            {isNotBlank(o.createdBy) && (
              <>
                {pipe}
                <span className="text-content-secondary">{o.createdBy}</span>
              </>
            )}
            {isNotBlank(o.warehouseName) && (
              <>
                {pipe}
                <span className="text-content-secondary">{o.warehouseName}</span>
              </>
            )}
          </span>
        ),
        subLabel:
          o.timeFrom || o.timeTo
            ? `${formatDateFromStringWithLocale(o.timeFrom) ?? ''} - ${formatDateFromStringWithLocale(o.timeTo) ?? ''}`
            : undefined,
        icon: outsideCurrentDate ? <SvgAlert /> : <SvgBill />,
        disabled: outsideCurrentDate,
        disabledReason: outsideCurrentDate
          ? tr('ExternalOrders.notValidForCurrentDate', 'Not available \u2014 the order does not cover the current date')
          : undefined,
        meta: { timeFrom: o.timeFrom, timeTo: o.timeTo },
      };
    })
      .filter((it): it is RingilDropdownItem => it !== null);
  }, [availableOrders, tr, showNoOrdersNow, disableOrdersOutsideCurrentDate]);

  const exclusiveItemIds = useMemo(
    () => items.filter(it => (it.meta as { isNoOrdersNow?: boolean } | undefined)?.isNoOrdersNow).map(it => it.id),
    [items],
  );

  const { minDate, maxDate } = useMemo(() => {
    let min: number | null = null;
    let max: number | null = null;
    items.forEach(item => {
      const meta = item.meta as { timeFrom?: string; timeTo?: string } | undefined;
      [meta?.timeFrom, meta?.timeTo].forEach(iso => {
        if (!iso) return;
        const ms = new Date(iso).getTime();
        if (Number.isNaN(ms)) return;
        if (min === null || ms < min) min = ms;
        if (max === null || ms > max) max = ms;
      });
    });
    return {
      minDate: min !== null ? new Date(min) : undefined,
      maxDate: max !== null ? new Date(max) : undefined,
    };
  }, [items]);

  const filterFn = (item: RingilDropdownItem): boolean => {
    if (!filterDate) return true;
    const meta = item.meta as { timeFrom?: string; timeTo?: string } | undefined;
    if (!meta?.timeFrom && !meta?.timeTo) {
      return true;
    }
    const target = dayjsFromLocalDateString(filterDate);
    const startOfDay = target.startOf('day').valueOf();
    const endOfDay = target.endOf('day').valueOf();
    const itemFromMs = meta.timeFrom ? new Date(meta.timeFrom).getTime() : null;
    const itemToMs = meta.timeTo ? new Date(meta.timeTo).getTime() : null;
    let result = true;
    if (itemFromMs != null && itemFromMs > endOfDay) result = false;
    if (itemToMs != null && itemToMs < startOfDay) result = false;
    return result;
  };

  const filterHeader = (
    <div className="flex flex-row gap-2 items-center">
      <span className="text-content-secondary text-xs whitespace-nowrap">
        {tr('OrdersComponent.externalOrdersFilterByDate', 'Filter by date')}
      </span>
      <div className="flex-1 min-w-0" data-testid={dataTestIdBase ? `${dataTestIdBase}List-filterHeader-date` : undefined}>
        <RingilCalendar
          value={filterDate}
          onChange={v => setFilterDate(v || null)}
          fullWidth
          baseZIndex={10000}
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>
      {filterDate && (
        <RingilButton
          mode="link"
          size="small"
          label={tr('OrdersComponent.externalOrdersFilterClear', 'Clear')}
          onClick={() => setFilterDate(null)}
          dataTestId={dataTestIdBase ? `${dataTestIdBase}List-filterHeader-dateClear` : undefined}
        />
      )}
    </div>
  );

  return (
    <RingilDropdown
      items={items}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      multiSelect
      selectAllEnabled
      showSelectedIcons
      showSelectedSubLabels
      readonly={readonly}
      dataTestIdBase={dataTestIdBase}
      filterHeader={filterHeader}
      filterFn={filterFn}
      exclusiveItemIds={exclusiveItemIds}
    />
  );
};

export default ExternalOrdersDropdown;